/**
 * Une affaire, et ses onglets.
 *
 * **Sept onglets.** Les trois premiers montrent l'affaire, la mémoire ses
 * acquis, l'assistant la travaille, l'activité la suit, le septième la gère.
 * Ce que la maquette dessine et qui reste absent
 * — conventions de métré, membres, cadre de sortie — n'a derrière lui aucun
 * point d'accès : l'afficher vide promettrait ce que la plateforme ne tient
 * pas.
 *
 * **L'onglet ouvert est dans l'adresse**, comme le tiroir des traitements. Un
 * rechargement en pleine lecture des documents ramenait à la synthèse, et le
 * lien qu'on envoyait n'ouvrait pas ce qu'on regardait.
 *
 * **L'assistant est une iframe**, décision `0013` : il garde l'apparence que
 * Chainlit lui donne. Le jeton de lancement est demandé au moment d'ouvrir
 * l'onglet — il ne vaut que deux minutes et une seule fois, donc le demander
 * d'avance le ferait expirer avant l'usage.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, ErreurApi } from "../api/client";
import type { AffaireVue, Travail } from "../api/types";
import { Chargement, Echec } from "../composants/etats";
import { Etat } from "../composants/etiquettes";
import { Activite } from "./affaire/Activite";
import { Chat } from "./affaire/Chat";
import { ChatFlottant } from "./affaire/ChatFlottant";
import { Demarrage } from "./affaire/Demarrage";
import { Documents } from "./affaire/Documents";
import { Parametres } from "./affaire/Parametres";
import { Synthese } from "./affaire/Synthese";
import { Instantanes } from "./affaire/Instantanes";
import { Glossaire } from "./affaire/Glossaire";
import { Inventaire } from "./affaire/Inventaire";
import { Reconstruction } from "./affaire/Reconstruction";
import { Memoire } from "./affaire/Memoire";
import { NavigateurOneDrive } from "./affaire/NavigateurOneDrive";
import { Traitements } from "./affaire/Traitements";
import { initiales } from "../composants/formats";
import { useDonnees, useSuivreLaFile, useVeille } from "../composants/donnees";
import { Lien, useParametre } from "../routeur";

/** Ce que le portail rend d'un membre — vérifié contre la réponse réelle,
 * non deviné : le champ s'appelle `nom`, et un `display_name` inventé aurait
 * rendu des initiales vides sans que rien n'échoue. */
interface Membre {
  user_id?: string;
  nom?: string;
  courriel?: string;
  role?: string;
}

/** Ce qu'on affiche d'un membre. Le rôle en dernier recours : mieux vaut
 * « OWNER » qu'un point d'interrogation. */
function nomDe(membre: Membre): string {
  return membre.nom || membre.courriel || membre.role || "";
}

const ONGLETS = [
  // **La synthèse d'abord.** La question qu'on se pose en ouvrant une affaire
  // n'est pas « quels fichiers ai-je ? » mais « puis-je chiffrer ? ». Ouvrir
  // sur les Documents répondait à la première.
  { clef: "synthese", libelle: "Synthèse" },
  // **Après la synthèse, et seulement si l'affaire a un dossier racine.** Le
  // démarrage audite ce dossier ; sans lui, il n'a rien à montrer, et l'onglet
  // est retiré au rendu (voir `onglets` plus bas).
  { clef: "demarrage", libelle: "Démarrage" },
  { clef: "documents", libelle: "Documents" },
  { clef: "instantanes", libelle: "Instantanés" },
  // Le glossaire lit l'instantané actif : son vocabulaire graphique (symboles,
  // repérages, légendes, unités). Il vient juste après lui, ce qu'il décrit.
  { clef: "glossaire", libelle: "Glossaire" },
  // L'inventaire des ouvrages à métrer, relevé du CCTP (source primaire) et
  // croisé à la DPGF. Juste avant la reconstruction : on sait QUOI métrer avant
  // de rebâtir le plan sur lequel on mesurera COMBIEN.
  { clef: "inventaire", libelle: "Inventaire des ouvrages" },
  // La reconstruction rebâtit le plan de chaque niveau — locaux, parois,
  // ouvertures — en direct. Après le glossaire : du vocabulaire au plan.
  { clef: "reconstruction", libelle: "Reconstruction" },
  // La mémoire entre ce que l'affaire possède (instantanés) et ce qui s'en
  // sert (assistant) : on vérifie les acquis avant de repartir en mission.
  { clef: "memoire", libelle: "Mémoire" },
  { clef: "assistant", libelle: "Assistant" },
  { clef: "activite", libelle: "Activité" },
  // **Le septième, et le dernier — c'est voulu.** Il porte ce qui gère
  // l'affaire plutôt que ce qui la travaille, jusqu'à sa destruction. Le
  // mettre plus tôt le ferait frôler en passant d'un onglet à l'autre.
  { clef: "parametres", libelle: "Paramètres" },
] as const;

type Onglet = (typeof ONGLETS)[number]["clef"];

export function Affaire({
  affaireId,
  assistantNatif = true,
}: {
  affaireId: string;
  assistantNatif?: boolean;
}) {
  // **L'onglet vit dans l'adresse.** Recharger en lisant les Documents
  // renvoyait à la synthèse ; et l'on ne pouvait pas envoyer « regarde les
  // pièces de cette affaire » — le lien ouvrait autre chose que ce qu'on
  // regardait. Un onglet inconnu retombe sur la synthèse plutôt que de rendre
  // une coquille vide : l'adresse vient parfois d'ailleurs.
  const [demande, poserOnglet] = useParametre("onglet", "synthese");
  const onglet = (ONGLETS.some((o) => o.clef === demande)
    ? demande : "synthese") as Onglet;
  // Le tiroir des traitements aussi : c'est une vue sur la file, pas une
  // saisie. Le rouvrir au rechargement ne coûte rien et évite de le
  // rechercher après un F5 passé à surveiller un import.
  const [tiroirDit, poserTiroirDit] = useParametre("traitements");
  const tiroir = tiroirDit === "1";
  // **La boîte OneDrive vit dans l'adresse.** Non par goût des URL bavardes :
  // lier un compte quitte le portail pour Microsoft, et l'on revient par une
  // navigation neuve. Sans cela, on retombe sur l'affaire, la boîte fermée et
  // la catégorie choisie perdue — ce qui donne à croire que la liaison a
  // échoué alors qu'elle a réussi.
  //
  // Elle passe par le même mécanisme que le reste depuis qu'il existe : elle
  // entretenait sa propre copie de l'adresse à coups de `replaceState`, ce qui
  // faisait deux écritures concurrentes sur la même barre. En remplacement,
  // pour la raison d'alors — ouvrir une boîte n'est pas un déplacement.
  const [oneDrive, poserOneDrive] = useParametre("onedrive");
  // Une amorce d'assistant apportée par une autre vue (ex. « échanger avec les
  // agents » sur un local de la reconstruction) : elle voyage par l'adresse,
  // seul canal entre onglets frères, et se consomme une fois (voir Chat).
  const [amorce, poserAmorce] = useParametre("amorce");
  const [rafraichi, rafraichir] = useState(0);

  // **La fenêtre flottante de l'assistant.** L'échange « avec les agents » ouvert
  // depuis une entité de la reconstruction flotte au-dessus de tout onglet, sans
  // quitter la vue. Son ouverture, sa réduction et sa position vivent ici et se
  // gardent au navigateur PAR AFFAIRE : un rechargement, un F5, une session neuve
  // la retrouvent où on l'a laissée (le fil, lui, revient du serveur).
  const cleChat = `chat-flottant:${affaireId}`;
  const [chatFlottant, poserChatFlottant] = useState<{
    ouvert: boolean; minimise: boolean; x: number | null; y: number | null;
  }>(() => {
    try {
      const brut = localStorage.getItem(cleChat);
      if (brut) {
        return { ouvert: false, minimise: false, x: null, y: null,
                 ...JSON.parse(brut) };
      }
    } catch { /* stockage indisponible : on repart d'un état neuf */ }
    return { ouvert: false, minimise: false, x: null, y: null };
  });
  // L'amorce est TRANSITOIRE : on ne la persiste pas — un rechargement ne doit
  // pas ré-amorcer l'ancienne entité.
  const [amorceFlottante, poserAmorceFlottante] = useState("");
  useEffect(() => {
    try { localStorage.setItem(cleChat, JSON.stringify(chatFlottant)); }
    catch { /* stockage indisponible : la fenêtre marche, sans mémoire */ }
  }, [cleChat, chatFlottant]);
  function ouvrirChatFlottant(amorceEntite: string) {
    poserAmorceFlottante(amorceEntite);
    poserChatFlottant((c) => ({ ...c, ouvert: true, minimise: false }));
  }
  // **L'assistant se monte une fois, puis se cache — jamais démonté.** Chaque
  // passage d'onglet détruisait l'iframe : revenir refaisait tout — un jeton
  // de lancement brûlé, le rechargement de Chainlit, la poignée websocket, la
  // relecture du fil — et le message en cours de frappe était perdu. Mesuré :
  // quatre visites, quatre jetons, le nœud DOM recréé à chaque fois.
  //
  // Un `ref` et non un état : quand l'onglet bascule, la page se rend déjà —
  // le drapeau est levé pendant ce rendu-là, sans rendu supplémentaire.
  const assistantNe = useRef(false);
  if (onglet === "assistant") assistantNe.current = true;

  const affaire = useDonnees<AffaireVue & { donnees?: AffaireVue }>(
    () => api.lire(`/api/v1/affaires/${affaireId}`),
    [affaireId],
  );

  // **Une seule veille, tenue par la page.** Les onglets s'y accrochent pour
  // relire ce qu'un ouvrier vient de changer ; le tiroir s'en sert pour
  // afficher la file. Deux relances sur le même point d'accès auraient doublé
  // les requêtes pour la même réponse.
  const veille = useVeille<Travail>(affaireId, (chemin) => api.lire(chemin));

  useSuivreLaFile(veille.empreinte, affaire.recharger);

  if (affaire.charge && !affaire.donnees) {
    return <div className="contenu"><Chargement lignes={4} /></div>;
  }
  if (affaire.erreur) {
    return (
      <div className="contenu">
        <Echec erreur={affaire.erreur} reessayer={affaire.recharger} />
      </div>
    );
  }

  // Le portail rend l'affaire seule ou dans l'enveloppe, selon la route ;
  // on accepte les deux plutôt que d'imposer une forme au serveur.
  const vue = (affaire.donnees?.donnees ?? affaire.donnees) as AffaireVue;

  const membres = (vue as AffaireVue & { membres?: Membre[] }).membres ?? [];
  // **Le démarrage n'existe que si l'affaire a un dossier racine.** Sinon
  // l'onglet est retiré de la barre, et une adresse `?onglet=demarrage` reçue
  // d'ailleurs retombe sur la synthèse plutôt que de rendre une coquille vide.
  const aRacine = !!vue.racine;
  const onglets = ONGLETS.filter((o) => o.clef !== "demarrage" || aRacine);
  const ongletActif: Onglet =
    onglet === "demarrage" && !aRacine ? "synthese" : onglet;
  const libelleOnglet =
    onglets.find((o) => o.clef === ongletActif)?.libelle ?? "";

  return (
    <>
      <div className="ecran">
        {/* Le bandeau de la référence : fil d'Ariane, identité, actions,
            onglets. Il ne défile pas — on garde les onglets sous la main en
            parcourant deux cents pièces. */}
        <div className="ecran__tete ecran__tete--affaire">
          <nav className="fil-simple" aria-label="Fil d'Ariane">
            <Lien vers="/affaires">Affaires</Lien>
            <span aria-hidden="true">/</span>
            <span className="mono">{vue.reference}</span>
            <span aria-hidden="true">/</span>
            <span>{libelleOnglet}</span>
          </nav>

          <div className="rangee rangee--espacee rangee--bas affaire__identite">
            <div style={{ minWidth: 0 }}>
              <div className="affaire__nom">
                <span className="affaire__reference">{vue.reference}</span>
                <h1 className="tronque">
                  {vue.label || <span className="faible">Sans intitulé</span>}
                </h1>
              </div>
              <div className="affaire__meta">
                <Etat genre="affaire" valeur={vue.state} />
                {membres.length > 0 && (
                  <span className="visages" title={membres.map(nomDe).join(", ")}>
                    {membres.slice(0, 3).map((membre, rang) => (
                      <span key={membre.user_id ?? rang} className="visage">
                        {initiales(nomDe(membre))}
                      </span>
                    ))}
                    {membres.length > 3 && (
                      <span className="visage visage--reste">
                        +{membres.length - 3}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            <div className="rangee">
              <button
                type="button"
                className="bouton"
                onClick={() => poserTiroirDit(tiroir ? "" : "1")}
              >
                Traitements
              </button>
              <button
                type="button"
                className="bouton bouton--primaire"
                onClick={() => poserOnglet("assistant")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M21 11.5a8 8 0 0 1-8 8H8l-5 3 1.4-4.4A8 8 0 0 1 11 3.5h2a8 8 0 0 1 8 8Z" />
                </svg>
                Ouvrir l'assistant
              </button>
            </div>
          </div>

          <div className="onglets" role="tablist">
            {onglets.map((item) => (
              <button
                key={item.clef}
                type="button"
                role="tab"
                className="onglet"
                aria-selected={ongletActif === item.clef}
                onClick={() => poserOnglet(item.clef)}
              >
                {item.libelle}
              </button>
            ))}
          </div>
        </div>

        <div className={`ecran__corps${ongletActif === "assistant" ? " ecran__corps--plein" : ""}`}>
          {ongletActif === "synthese" && (
            <Synthese
              completude={vue.completude}
              etat={vue.state}
              veille={veille}
              versDocuments={() => poserOnglet("documents")}
            />
          )}
          {ongletActif === "demarrage" && vue.racine && (
            <Demarrage
              affaireId={affaireId}
              racine={vue.racine}
              etat={vue.state}
              veille={veille}
            />
          )}
          {ongletActif === "documents" && (
            <Documents
              key={rafraichi}
              affaireId={affaireId}
              etat={vue.state}
              veille={veille}
              ouvrirOneDrive={(categorie) => poserOneDrive(categorie, true)}
            />
          )}
          {ongletActif === "instantanes" && (
            <Instantanes affaireId={affaireId} veille={veille} />
          )}
          {ongletActif === "glossaire" && (
            <Glossaire
              affaireId={affaireId}
              veille={veille}
              profil={vue.profil ?? null}
              surAffectation={() => affaire.recharger()}
            />
          )}
          {ongletActif === "inventaire" && (
            <Inventaire affaireId={affaireId} onEchanger={ouvrirChatFlottant} />
          )}
          {ongletActif === "reconstruction" && (
            <Reconstruction affaireId={affaireId}
                            onEchanger={ouvrirChatFlottant} />
          )}
          {ongletActif === "memoire" && <Memoire affaireId={affaireId} />}
          {/* **Le natif d'abord ; l'iframe est le repli** (`0015`, P22.4).
              Le chat natif est state-first : pas besoin de le garder monté,
              rouvrir l'onglet relit le journal — c'est le même code que le
              premier rendu. L'iframe, elle, garde sa scène vivante. */}
          {assistantNatif && ongletActif === "assistant" && (
            <Chat affaireId={affaireId} amorce={amorce}
                  onAmorceConsommee={() => poserAmorce("", true)} />
          )}
          {!assistantNatif && assistantNe.current && (
            <div
              className="assistant__scene"
              hidden={ongletActif !== "assistant"}
            >
              <Assistant affaireId={affaireId} />
            </div>
          )}
          {ongletActif === "activite" && (
            <Activite affaireId={affaireId} veille={veille} />
          )}
          {ongletActif === "parametres" && (
            <Parametres
              affaireId={affaireId}
              etat={vue.state}
              rafraichir={affaire.recharger}
            />
          )}
        </div>
      </div>

      {tiroir && (
        <Traitements veille={veille} fermer={() => poserTiroirDit("")} />
      )}
      {oneDrive !== "" && (
        <NavigateurOneDrive
          affaireId={affaireId}
          categorieInitiale={oneDrive}
          fermer={() => poserOneDrive("", true)}
          importe={() => {
            rafraichir((n) => n + 1);
            poserTiroirDit("1");
          }}
        />
      )}
      {chatFlottant.ouvert && (
        <ChatFlottant
          affaireId={affaireId}
          amorce={amorceFlottante}
          onAmorceConsommee={() => poserAmorceFlottante("")}
          minimise={chatFlottant.minimise}
          position={{ x: chatFlottant.x, y: chatFlottant.y }}
          onDeplacer={(x, y) => poserChatFlottant((c) => ({ ...c, x, y }))}
          onBasculerMinimise={() =>
            poserChatFlottant((c) => ({ ...c, minimise: !c.minimise }))}
          onFermer={() => poserChatFlottant((c) => ({ ...c, ouvert: false }))}
        />
      )}
    </>
  );
}

/**
 * L'assistant, monté sous la même origine.
 *
 * Le jeton de lancement s'échange contre une ouverture : il est aléatoire,
 * valable deux minutes, à usage unique, et lié au couple utilisateur/affaire.
 * C'est ce qui fait qu'une URL d'assistant n'est pas une autorisation.
 */
function Assistant({ affaireId }: { affaireId: string }) {
  const [adresse, poserAdresse] = useState<string>("");
  const [erreur, poserErreur] = useState("");

  const ouvrir = useCallback(async () => {
    poserErreur("");
    try {
      const issue = await api.ecrire<{ url?: string; donnees?: { url?: string } }>(
        `/api/v1/affaires/${affaireId}/assistant`,
      );
      const url = issue.url ?? issue.donnees?.url;
      poserAdresse(url ?? "/assistant/");
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi
          ? exc.message
          : "L'assistant n'a pas pu être ouvert.",
      );
    }
  }, [affaireId]);

  useEffect(() => {
    void ouvrir();
  }, [ouvrir]);

  if (erreur) return <Echec erreur={new Error(erreur)} reessayer={ouvrir} />;
  if (!adresse) return <Chargement lignes={2} />;

  return (
    <div className="assistant__cadre">
      <iframe
        className="assistant"
        src={adresse}
        title="Assistant métré"
        // L'assistant est servi par le portail, sous la même origine : il n'a
        // pas besoin d'être isolé de nous, et l'isoler casserait son websocket.
        allow="clipboard-write"
      />
    </div>
  );
}
